import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import API from "../../lib/api";
import { PortalPage, Section, Pill } from "../../components/portal/PortalPage";
import { AttachmentGallery } from "../../components/portal/AttachmentGallery";
import { ClientTimeline } from "../../components/portal/ClientTimeline";
import { ClientNotesThread } from "../../components/portal/ClientNotesThread";
import { InvoiceSummaryCard } from "../../components/portal/InvoiceSummaryCard";
import { usePortal } from "../../components/portal/usePortal";
import { useTeamLabel } from "../../components/portal/PortalBrand";
import { fmtDate, PROJECT_STATUS, TASK_STATUS, titleCase } from "../../components/portal/portalUtils";
import { ProjectProgressBar } from "./ClientProjectsPage";
import { MapPin, ChatCircle, ClipboardText, Flag, CheckCircle, Circle, CalendarBlank, UserCircle, Truck, ListChecks } from "@phosphor-icons/react";
import { DetailSkeleton } from "../../components/Skeletons";

const TASK_GROUPS = [["active", "In progress", ["in_progress", "completed_by_vendor"]], ["upcoming", "Upcoming", ["not_started", "assigned", "on_hold"]], ["done", "Completed", ["approved"]]];

function TaskRow({ t }) {
  const teamLabel = useTeamLabel();
  const done = t.status === "approved";
  return (
    <li className="flex items-start gap-3 py-2.5" data-testid={`task-row-${t.id}`}>
      {done ? <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" /> : <Circle size={18} className={`shrink-0 mt-0.5 ${t.status === "in_progress" ? "text-primary" : "text-muted-foreground/50"}`} weight={t.status === "in_progress" ? "duotone" : "regular"} />}
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${done ? "text-muted-foreground line-through" : "font-medium"}`}>{t.title}</p>
        <p className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
          {t.property_area && <span>{t.property_area}</span>}
          {t.due_date && <span className="flex items-center gap-0.5"><CalendarBlank size={10} /> due {fmtDate(t.due_date)}</span>}
          {t.completed_at && <span>done {fmtDate(t.completed_at)}</span>}
          {t.assigned_to_name ? <span className="flex items-center gap-0.5"><Truck size={10} /> {t.assigned_to_name}</span> : t.assigned ? <span className="text-emerald-700 dark:text-emerald-400">{teamLabel.assigned}</span> : null}
          {t.photo_count > 0 && <span>{t.photo_count} photo{t.photo_count > 1 ? "s" : ""}</span>}
        </p>
      </div>
      <Pill map={TASK_STATUS} value={t.status} />
    </li>
  );
}

export default function ClientProjectDetailPage() {
  const { id } = useParams();
  const { perms } = usePortal();
  const [p, setP] = useState(null);

  const load = useCallback(() => API.get(`/client-hub/projects/${id}`).then(({ data }) => setP(data)).catch(() => setP(false)), [id]);
  useEffect(() => { load(); }, [load]);

  if (p === null) return <DetailSkeleton />;
  if (p === false) return <PortalPage title="Project not found" back={{ to: "/portal/projects", label: "Projects" }} />;

  const tasks = p.tasks || [];
  return (
    <PortalPage
      title={p.name}
      subtitle={<span className="flex items-center gap-2 flex-wrap"><span className="font-mono">{p.reference}</span><Pill map={PROJECT_STATUS} value={p.status} />{p.request && <Link to={`/portal/requests/${p.request.id}`} className="text-[11px] text-primary hover:underline flex items-center gap-1" data-testid="project-request-link"><ClipboardText size={11} /> From request {p.request.reference}</Link>}</span>}
      back={{ to: "/portal/projects", label: "Projects" }}
      testid="project-detail"
    >
      <Section title="Progress" className="mb-6" testid="project-progress-section">
        <ProjectProgressBar progress={p.progress} />
        <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
          <div className="border border-border rounded-md p-3"><p className="text-muted-foreground flex items-center gap-1"><CalendarBlank size={12} /> Start</p><p className="font-semibold mt-1">{p.start_date ? fmtDate(p.start_date) : "TBC"}</p></div>
          <div className="border border-border rounded-md p-3"><p className="text-muted-foreground">Target completion</p><p className="font-semibold mt-1">{p.target_date ? fmtDate(p.target_date) : "TBC"}</p></div>
          <div className="border border-border rounded-md p-3"><p className="text-muted-foreground">Completed</p><p className="font-semibold mt-1">{p.actual_completion_date ? fmtDate(p.actual_completion_date) : "—"}</p></div>
        </div>
      </Section>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Scope of Works" testid="project-scope">
            <p className="text-sm whitespace-pre-wrap">{p.scope || <span className="text-muted-foreground">Scope will appear here once agreed.</span>}</p>
            <dl className="grid sm:grid-cols-2 gap-3 mt-4 text-xs">
              <div><dt className="text-muted-foreground">Type</dt><dd className="font-medium">{titleCase(p.project_type)}</dd></div>
              <div><dt className="text-muted-foreground">Priority</dt><dd className="font-medium">{titleCase(p.priority)}</dd></div>
            </dl>
          </Section>

          <Section title={<span className="flex items-center gap-1.5"><ListChecks size={14} /> Tasks ({tasks.length})</span>} testid="project-tasks">
            {tasks.length === 0 ? <p className="text-xs text-muted-foreground">Tasks will be listed here as our team plans the works.</p> : (
              <div className="space-y-4">
                {TASK_GROUPS.map(([key, label, statuses]) => {
                  const rows = tasks.filter((t) => statuses.includes(t.status));
                  if (!rows.length) return null;
                  return (
                    <div key={key} data-testid={`task-group-${key}`}>
                      <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">{label} · {rows.length}</p>
                      <ul className="divide-y divide-border">{rows.map((t) => <TaskRow key={t.id} t={t} />)}</ul>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          <Section title={`Photos (${(p.photos || []).length})`} testid="project-photos">
            <AttachmentGallery attachments={p.photos || []} emptyText="Progress and before/after photos will appear here as work is carried out." />
          </Section>

          <Section title="Documents & Plans" testid="project-documents">
            <AttachmentGallery attachments={p.documents || []} emptyText="Drawings, certificates and completion documents will appear here." testid="project-doc-gallery" />
          </Section>

          <Section title={<span className="flex items-center gap-1.5"><ChatCircle size={14} /> Conversation</span>} testid="project-comments">
            <ClientNotesThread items={p.client_comments || []} postUrl={`/client-hub/projects/${id}/comments`} folder="client-projects" canMessage={perms.can_message} onSent={load} testPrefix="project-comment" />
          </Section>
        </div>

        <div className="space-y-6">
          <Section title={<span className="flex items-center gap-1.5"><Flag size={14} /> Milestones</span>} testid="project-milestones">
            {(p.milestones || []).length === 0 ? <p className="text-xs text-muted-foreground">No milestones set yet.</p> : (
              <ol className="space-y-3">
                {p.milestones.map((m) => {
                  const done = m.completed || m.status === "completed";
                  return (
                    <li key={m.id} className="flex gap-2" data-testid={`milestone-${m.id}`}>
                      {done ? <CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" /> : <Circle size={16} className="text-muted-foreground/50 shrink-0 mt-0.5" />}
                      <div className="min-w-0">
                        <p className={`text-xs font-medium ${done ? "text-muted-foreground line-through" : ""}`}>{m.title}</p>
                        <p className="text-[10px] text-muted-foreground">{m.target_date ? `Target ${fmtDate(m.target_date)}` : ""}{done && m.completed_date ? ` · reached ${fmtDate(m.completed_date)}` : ""}{!done && m.status === "missed" ? " · overdue" : ""}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Section>
          <Section title="Property">
            <p className="text-sm font-medium flex items-start gap-1.5"><MapPin size={16} className="text-primary shrink-0 mt-0.5" /> {p.address || "—"}</p>
          </Section>
          <Section title="Project Team" testid="project-team">
            {p.manager_name && <p className="text-xs flex items-center gap-1.5"><UserCircle size={14} className="text-primary" /> Managed by <strong>{p.manager_name}</strong></p>}
            {p.contractor_visible && (p.team || []).length > 0 ? (
              <ul className="mt-2 space-y-1">{p.team.map((n) => <li key={n} className="text-xs flex items-center gap-1.5"><Truck size={12} className="text-muted-foreground" /> {n}</li>)}</ul>
            ) : tasks.some((t) => t.assigned) ? <p className="text-[11px] text-muted-foreground mt-2">Vetted contractors from our network are allocated to the tasks on this project.</p> : <p className="text-[11px] text-muted-foreground mt-2">Contractors to be confirmed.</p>}
          </Section>
          <InvoiceSummaryCard invoices={p.invoices} testid="project-invoice" />
          <Section title="Activity"><ClientTimeline entries={p.timeline} /></Section>
        </div>
      </div>
    </PortalPage>
  );
}
